import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./Footer.css";
import { getFooter } from "../api/footer";

const Footer = () => {
  const [footerData, setFooterData] = useState(null);
  const [footerError, setFooterError] = useState(null);

  const strapiBaseUrl = process.env.REACT_APP_STRAPI_URL || "";

  const buildMediaUrl = (media) => {
    if (!media || !media.url) return undefined;
    return `${strapiBaseUrl}${media.url}`;
  };

  useEffect(() => {
    const loadFooter = async () => {
      try {
        const res = await getFooter();
        setFooterData(res.data);
      } catch (err) {
        console.error("Failed to load footer from Strapi:", err);
        setFooterError(err.message);
      }
    };

    loadFooter();
  }, []);

  const brandImageUrl = buildMediaUrl(footerData?.brandImage);
  const brandImageAlt =
    footerData?.brandImage?.alternativeText || "Footer brand";

  const columns = footerData?.columns || [];

  const brandBadge = footerData?.brandBadge;
  const brandBadgeLogoUrl = buildMediaUrl(brandBadge?.logo);
  const brandBadgeLinkUrl = brandBadge?.logoUrl || null; 
  const textBefore = brandBadge?.textBefore || "";
  const textAfter = brandBadge?.textAfter || "";

  const copyrightText = footerData?.copyrightText || "";

  const renderLink = (link) => {
    if (!link || !link.url) return null;

    const isExternal =
      link.isExternal ||
      link.url.startsWith("http://") ||
      link.url.startsWith("https://");

    if (isExternal) {
      return (
        <a href={link.url} target="_blank" rel="noopener noreferrer">
          {link.label}
        </a>
      );
    }

    return <Link to={link.url}>{link.label}</Link>;
  };

  // The footer schema has no URL field on a column title; title-only clickable
  // columns (FAQ, inLoop AI) are modeled in the CMS as a single link whose
  // label repeats the column title.
  const getTitleLink = (column) => {
    const links = column.links || [];
    return links.length === 1 &&
      links[0].label === column.title &&
      links[0].url
      ? links[0]
      : null;
  };

  if (!footerData && !footerError) {
    return null;
  }

  return (
    <footer className="footer">
      <div className="footer-main">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              {brandImageUrl && (
                <img
                  src={brandImageUrl}
                  alt={brandImageAlt}
                  className="footer-brand-image"
                />
              )}
            </div>

            <div className="footer-links">
              {columns.map((column) => {
                const titleLink = getTitleLink(column);

                return (
                  <div className="footer-column" key={column.id}>
                    {titleLink ? (
                      <h4 className="footer-title">{renderLink(titleLink)}</h4>
                    ) : (
                      <>
                        {column.title && (
                          <h4 className="footer-title">{column.title}</h4>
                        )}
                        <ul className="footer-menu">
                          {(column.links || []).map((link) => (
                            <li key={link.id}>{renderLink(link)}</li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="footer-bottom">
        <div className="container">
          <div className="footer-bottom-content">
            <div className="footer-left">
              {textBefore && <span className="footer-text">{textBefore}</span>}

              {brandBadgeLogoUrl &&
                (brandBadgeLinkUrl ? (
                  <a
                    href={brandBadgeLinkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="footer-adesso-link"
                  >
                    <img
                      src={brandBadgeLogoUrl}
                      alt={brandBadge?.logo?.alternativeText || "Brand badge"}
                      className="footer-adesso-logo"
                    />
                  </a>
                ) : (
                  <span className="footer-adesso-link">
                    <img
                      src={brandBadgeLogoUrl}
                      alt={brandBadge?.logo?.alternativeText || "Brand badge"}
                      className="footer-adesso-logo"
                    />
                  </span>
                ))}

              {textAfter && <span className="footer-text">{textAfter}</span>}
            </div>

            <div className="footer-right">
              {copyrightText && (
                <span className="footer-copyright">{copyrightText}</span>
              )}
            </div>
          </div>

          {footerError && (
            <div
              className="footer-error"
              style={{ marginTop: 8, fontSize: 12 }}
            >
              Footer could not be loaded: {footerError}
            </div>
          )}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
